import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useCustomToast,
} from '@elkaso-app/web-design';
import { useCreateCrmMasterCuisineApi } from 'apis/crm-master-cuisines';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';

interface CreateMasterCuisineModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  region?: string;
  country?: string;
}

export const CreateMasterCuisineModal: React.FC<CreateMasterCuisineModalProps> = ({ isOpen, onClose }) => {
  const toast = useCustomToast();

  const { mutate: createMasterCuisine, isLoading: isCreating } = useCreateCrmMasterCuisineApi({
    onSuccess: (data) => {
      toast({
        title: 'Success!',
        description: `Master cuisine "${data.name}" has been created successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      reset();
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create master cuisine. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      region: '',
      country: '',
    },
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        name: data.name.trim(),
        region: data.region?.trim() || undefined,
        country: data.country?.trim() || undefined,
      };
      createMasterCuisine(payload);
    },
    [createMasterCuisine]
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Master Cuisine</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Cuisine Name</FormLabel>
              <Input
                placeholder='Enter cuisine name'
                {...register('name', {
                  required: 'Cuisine name is required',
                  maxLength: {
                    value: 255,
                    message: 'Cuisine name must be 255 characters or less',
                  },
                  validate: (value) => {
                    const trimmed = value.trim();
                    return trimmed.length > 0 || 'Cuisine name cannot be empty';
                  },
                })}
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.region}>
              <FormLabel>Region</FormLabel>
              <Input
                placeholder='Enter region (optional)'
                {...register('region', {
                  maxLength: {
                    value: 255,
                    message: 'Region must be 255 characters or less',
                  },
                })}
              />
              <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.country}>
              <FormLabel>Country</FormLabel>
              <Input
                placeholder='Enter country (optional)'
                {...register('country', {
                  maxLength: {
                    value: 255,
                    message: 'Country must be 255 characters or less',
                  },
                })}
              />
              <FormErrorMessage>{errors.country?.message}</FormErrorMessage>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onCloseModal} isDisabled={isCreating}>
            Cancel
          </Button>
          <Button
            colorScheme='blue'
            onClick={handleSubmit(onSubmit)}
            isLoading={isCreating}
            loadingText='Creating...'
            isDisabled={!isValid}>
            Create Master Cuisine
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
